import React, { useEffect, useState, useMemo } from 'react';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Eye, Download, Edit, Trash2, History, FileSpreadsheet, Filter, X } from 'lucide-react';

export default function OfflinePayments() {
  const { getAuthHeaders, user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isAuditOpen, setIsAuditOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [editingPayment, setEditingPayment] = useState(null);
  const [agencies, setAgencies] = useState([]);  // For Master Agency
  const [formData, setFormData] = useState({
    beneficiary: '',
    payment_type: '',
    transaction_id: '',
    amount: '',
    txn_date: '',
    account_holder_name: '',
    remarks: '',
    application_id: '',
    student_name: '',
    agency_id: ''  // For Master Agency to select agency
  });
  const [paySlipFile, setPaySlipFile] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownControls, setDropdownControls] = useState({});
  
  // Filter states
  const [agencyFilter, setAgencyFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [allAgencies, setAllAgencies] = useState([]);
  const [exporting, setExporting] = useState(false);

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Month options
  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  // Year options (current year and past 3 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 4 }, (_, i) => (currentYear - i).toString());

  useEffect(() => {
    fetchPayments();
    fetchDropdownControls();
    fetchAllAgencies();
    if (user?.role === 'master_agency') {
      fetchAgencies();
    }
  }, [user?.role]);

  const fetchDropdownControls = async () => {
    try {
      const response = await axios.get(`${API_URL}/dropdown-controls`, {
        headers: getAuthHeaders()
      });
      const controlsMap = {};
      response.data.forEach(control => {
        controlsMap[control.category] = control.values;
      });
      setDropdownControls(controlsMap);
    } catch (error) {
      console.error('Failed to fetch dropdown controls');
    }
  };

  const fetchAgencies = async () => {
    try {
      const response = await axios.get(`${API_URL}/master-agency/agencies`, {
        headers: getAuthHeaders()
      });
      setAgencies(response.data);
    } catch (error) {
      console.error('Failed to fetch agencies');
    }
  };

  const fetchAllAgencies = async () => {
    try {
      let url = `${API_URL}/agencies`;
      if (user?.role === 'master_agency') {
        url = `${API_URL}/master-agency/agencies`;
      }
      const response = await axios.get(url, {
        headers: getAuthHeaders()
      });
      setAllAgencies(response.data);
    } catch (error) {
      console.error('Failed to fetch agencies for filter');
    }
  };

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API_URL}/offline-payments`, {
        headers: getAuthHeaders()
      });
      setPayments(response.data);
    } catch (error) {
      toast.error('Failed to fetch payments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      if (paySlipFile) {
        formDataToSend.append('pay_slip', paySlipFile);
      }

      if (editingPayment) {
        await axios.put(`${API_URL}/offline-payments/${editingPayment.id}`, formDataToSend, {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('Payment updated successfully');
      } else {
        await axios.post(`${API_URL}/offline-payments`, formDataToSend, {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('Payment added successfully');
      }

      setIsFormOpen(false);
      resetForm();
      fetchPayments();
    } catch (error) {
      toast.error('Failed to save payment');
    }
  };

  const handleEdit = (payment) => {
    setEditingPayment(payment);
    setFormData({
      beneficiary: payment.beneficiary,
      payment_type: payment.payment_type,
      transaction_id: payment.transaction_id,
      amount: payment.amount,
      txn_date: payment.txn_date,
      account_holder_name: payment.account_holder_name || '',
      remarks: payment.remarks || '',
      application_id: payment.application_id || '',
      student_name: payment.student_name || ''
    });
    setIsFormOpen(true);
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;
    
    try {
      await axios.delete(`${API_URL}/offline-payments/${paymentId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Payment deleted successfully');
      fetchPayments();
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  const handleUpdateStatus = async (paymentId, status) => {
    const remarks = prompt(`Enter remarks for ${status} status (optional):`);
    
    try {
      await axios.put(
        `${API_URL}/offline-payments/${paymentId}/status`,
        { payment_status: status, remarks },
        { headers: getAuthHeaders() }
      );
      toast.success('Payment status updated');
      fetchPayments();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDownloadPaySlip = async (fileId) => {
    try {
      const response = await axios.get(`${API_URL}/files/${fileId}`, {
        headers: getAuthHeaders()
      });
      const link = document.createElement('a');
      link.href = `data:application/octet-stream;base64,${response.data.content}`;
      link.download = response.data.filename;
      link.click();
      toast.success('Pay slip downloaded');
    } catch (error) {
      toast.error('Failed to download pay slip');
    }
  };

  const handleViewAuditLogs = async (paymentId) => {
    try {
      const response = await axios.get(`${API_URL}/offline-payments/${paymentId}/audit-logs`, {
        headers: getAuthHeaders()
      });
      setAuditLogs(response.data);
      setIsAuditOpen(true);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    }
  };

  const resetForm = () => {
    setFormData({
      beneficiary: '',
      payment_type: '',
      transaction_id: '',
      amount: '',
      txn_date: '',
      account_holder_name: '',
      remarks: '',
      application_id: '',
      student_name: '',
      agency_id: ''
    });
    setPaySlipFile(null);
    setEditingPayment(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  // Enhanced filtering with agency, month, and year
  const filteredPayments = useMemo(() => {
    return payments.filter(payment => {
      // Text search filter
      const matchesSearch = searchTerm === '' || 
        payment.transaction_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.beneficiary?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.agency_name?.toLowerCase().includes(searchTerm.toLowerCase());

      // Agency filter
      const matchesAgency = agencyFilter === 'all' || payment.agency_id === agencyFilter;

      // Month/Year filter based on txn_date (DD-MM-YYYY format)
      let matchesMonth = true;
      let matchesYear = true;
      if (payment.txn_date && (monthFilter !== 'all' || yearFilter !== 'all')) {
        const parts = payment.txn_date.split('-');
        if (parts.length === 3) {
          const [day, month, year] = parts;
          if (monthFilter !== 'all') {
            matchesMonth = month === monthFilter;
          }
          if (yearFilter !== 'all') {
            matchesYear = year === yearFilter;
          }
        }
      }

      return matchesSearch && matchesAgency && matchesMonth && matchesYear;
    });
  }, [payments, searchTerm, agencyFilter, monthFilter, yearFilter]);

  // Export to Excel
  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (agencyFilter !== 'all') params.append('agency_id', agencyFilter);
      if (monthFilter !== 'all') params.append('month', monthFilter);
      if (yearFilter !== 'all') params.append('year', yearFilter);

      const response = await axios.get(
        `${API_URL}/offline-payments/export/excel?${params.toString()}`,
        {
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'offline_payments.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded successfully');
    } catch (error) {
      toast.error('Failed to export payments');
    } finally {
      setExporting(false);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setAgencyFilter('all');
    setMonthFilter('all');
    setYearFilter('all');
    setSearchTerm('');
  };

  // Check if any filter is active
  const hasActiveFilters = agencyFilter !== 'all' || monthFilter !== 'all' || yearFilter !== 'all' || searchTerm !== '';

  return (
    <Sidebar>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Offline Payments
            </h1>
            <p className="text-slate-600 mt-2">Manage offline payment transactions</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="toggle-filters-button"
              className={showFilters ? 'bg-purple-50 border-purple-300' : ''}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <span className="ml-2 bg-purple-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  !
                </span>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={handleExportExcel}
              disabled={exporting}
              data-testid="export-excel-button"
              className="text-green-600 hover:text-green-700 hover:bg-green-50"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              {exporting ? 'Exporting...' : 'Export Excel'}
            </Button>
            <Dialog open={isFormOpen} onOpenChange={(open) => {
              setIsFormOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-payment-button" className="bg-[#0052CC] hover:bg-[#0043A8] text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Payment
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingPayment ? 'Edit Payment' : 'Add Offline Payment'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Agency Selector for Master Agency */}
                  {user?.role === 'master_agency' && !editingPayment && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
                      <Label htmlFor="agency_id" className="text-purple-700 font-medium">Select Agency *</Label>
                      <Select value={formData.agency_id} onValueChange={(value) => setFormData({...formData, agency_id: value})}>
                        <SelectTrigger id="agency_id" className="mt-2">
                          <SelectValue placeholder="Select Agency for this payment" />
                        </SelectTrigger>
                        <SelectContent>
                          {agencies.map(agency => (
                            <SelectItem key={agency.id} value={agency.id}>{agency.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-purple-600 mt-1">Choose which agency this payment is for</p>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="beneficiary">Beneficiary *</Label>
                      <Select value={formData.beneficiary} onValueChange={(value) => setFormData({...formData, beneficiary: value})} required>
                        <SelectTrigger id="beneficiary">
                          <SelectValue placeholder="Select Beneficiary" />
                        </SelectTrigger>
                        <SelectContent>
                          {(dropdownControls.beneficiaries || ['Blackboard', 'Mangalayatan University']).map(b => (
                            <SelectItem key={b} value={b}>{b}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="payment_type">Payment Type *</Label>
                      <Select value={formData.payment_type} onValueChange={(value) => setFormData({...formData, payment_type: value})} required>
                        <SelectTrigger id="payment_type">
                          <SelectValue placeholder="Select Type" />
                        </SelectTrigger>
                        <SelectContent>
                          {(dropdownControls.payment_types || ['UPI', 'NEFT', 'IMPS', 'Cash', 'RTGS']).map(t => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                  </div>
                  <div>
                    <Label htmlFor="transaction_id">Transaction ID *</Label>
                    <Input
                      id="transaction_id"
                      value={formData.transaction_id}
                      onChange={(e) => setFormData({...formData, transaction_id: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="amount">Amount (₹) *</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({...formData, amount: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="txn_date">Transaction Date (DD-MM-YYYY) *</Label>
                    <Input
                      id="txn_date"
                      value={formData.txn_date}
                      onChange={(e) => setFormData({...formData, txn_date: e.target.value})}
                      placeholder="25-01-2025"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="account_holder_name">Account Holder Name</Label>
                    <Input
                      id="account_holder_name"
                      value={formData.account_holder_name}
                      onChange={(e) => setFormData({...formData, account_holder_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="student_name">Student Name</Label>
                    <Input
                      id="student_name"
                      value={formData.student_name}
                      onChange={(e) => setFormData({...formData, student_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label htmlFor="application_id">Application ID</Label>
                    <Input
                      id="application_id"
                      value={formData.application_id}
                      onChange={(e) => setFormData({...formData, application_id: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="pay_slip">Pay Slip (Image Only) *</Label>
                  <Input
                    id="pay_slip"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setPaySlipFile(e.target.files[0])}
                    required={!editingPayment}
                  />
                </div>
                <div>
                  <Label htmlFor="remarks">Remarks</Label>
                  <Textarea
                    id="remarks"
                    value={formData.remarks}
                    onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                    rows={3}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsFormOpen(false)}>
                    Close
                  </Button>
                  <Button type="submit" className="bg-[#0052CC] hover:bg-[#0043A8] text-white">
                    {editingPayment ? 'Update' : 'Add'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6 shadow-sm" data-testid="filters-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-slate-700 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filter Payments
              </h3>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 hover:text-slate-700">
                  <X className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Agency Filter - visible for Admin, Sub Admin, Master Agency */}
              {user?.role !== 'agency' && (
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Agency</Label>
                  <Select value={agencyFilter} onValueChange={setAgencyFilter}>
                    <SelectTrigger data-testid="agency-filter">
                      <SelectValue placeholder="All Agencies" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Agencies</SelectItem>
                      {allAgencies.map(agency => (
                        <SelectItem key={agency.id} value={agency.id}>{agency.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Month Filter */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Month</Label>
                <Select value={monthFilter} onValueChange={setMonthFilter}>
                  <SelectTrigger data-testid="month-filter">
                    <SelectValue placeholder="All Months" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Months</SelectItem>
                    {months.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Year Filter */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Year</Label>
                <Select value={yearFilter} onValueChange={setYearFilter}>
                  <SelectTrigger data-testid="year-filter">
                    <SelectValue placeholder="All Years" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Years</SelectItem>
                    {years.map(y => (
                      <SelectItem key={y} value={y}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Search */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Search</Label>
                <Input
                  placeholder="Transaction ID, Student..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-input"
                />
              </div>
            </div>
            
            {/* Active Filters Summary */}
            {hasActiveFilters && (
              <div className="mt-4 pt-4 border-t border-slate-200 flex items-center gap-2 text-sm text-slate-600">
                <span>Showing {filteredPayments.length} of {payments.length} payments</span>
              </div>
            )}
          </div>
        )}

        {/* Quick Search (when filters hidden) */}
        {!showFilters && (
          <div className="mb-6">
            <Input
              placeholder="Search by Transaction ID, Beneficiary, Student Name, or Agency..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
              data-testid="quick-search-input"
            />
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">Loading payments...</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="payments-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">File</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Payment Type</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Transaction ID</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Amount</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Txn Date</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Beneficiary</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Student</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Agency</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Added On</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Status</th>
                    <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map((payment) => (
                    <tr key={payment.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                      <td className="p-4 align-middle text-sm whitespace-nowrap">
                        {payment.pay_slip_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownloadPaySlip(payment.pay_slip_id)}
                            title="Download Pay Slip"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                      <td className="p-4 align-middle text-sm text-slate-700 whitespace-nowrap">{payment.payment_type}</td>
                      <td className="p-4 align-middle text-sm text-slate-700 font-medium whitespace-nowrap">{payment.transaction_id}</td>
                      <td className="p-4 align-middle text-sm text-slate-700 whitespace-nowrap">₹{payment.amount}</td>
                      <td className="p-4 align-middle text-sm text-slate-600 whitespace-nowrap">{payment.txn_date}</td>
                      <td className="p-4 align-middle text-sm text-slate-600 whitespace-nowrap">{payment.beneficiary}</td>
                      <td className="p-4 align-middle text-sm text-slate-600 whitespace-nowrap">{payment.student_name || '-'}</td>
                      <td className="p-4 align-middle text-sm text-slate-600 whitespace-nowrap">{payment.agency_name}</td>
                      <td className="p-4 align-middle text-sm text-slate-600 whitespace-nowrap">
                        {new Date(payment.created_at).toLocaleDateString()}
                      </td>
                      <td className="p-4 align-middle text-sm whitespace-nowrap">
                        {(user?.role === 'admin' || user?.role === 'sub_admin' || user?.role === 'master_agency') ? (
                          <Select
                            value={payment.payment_status}
                            onValueChange={(value) => handleUpdateStatus(payment.id, value)}
                          >
                            <SelectTrigger className={`w-32 ${getStatusColor(payment.payment_status)}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">Pending</SelectItem>
                              <SelectItem value="approved">Approved</SelectItem>
                              <SelectItem value="rejected">Rejected</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className={`inline-block px-2 py-1 text-xs rounded ${getStatusColor(payment.payment_status)}`}>
                            {payment.payment_status.toUpperCase()}
                          </span>
                        )}
                      </td>
                      <td className="p-4 align-middle text-sm whitespace-nowrap">
                        <div className="flex gap-1">
                          {(user?.role === 'admin' || user?.role === 'sub_admin' || user?.role === 'master_agency') && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEdit(payment)}
                                title="Edit Payment"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewAuditLogs(payment.id)}
                                title="View History"
                              >
                                <History className="w-4 h-4" />
                              </Button>
                              {/* Delete button - NOT for Agency users */}
                              {user?.role !== 'agency' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDelete(payment.id)}
                                  className="text-red-600 hover:text-red-700"
                                  title="Delete Payment"
                                  data-testid={`delete-payment-${payment.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredPayments.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                No payments found.
              </div>
            )}
          </div>
        )}

        {/* Audit Logs Dialog */}
        <Dialog open={isAuditOpen} onOpenChange={setIsAuditOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Payment History</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {auditLogs.length > 0 ? (
                auditLogs.map((log, index) => (
                  <div key={index} className="border border-slate-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="text-sm font-medium text-slate-900">{log.action_type.replace(/_/g, ' ').toUpperCase()}</span>
                        <p className="text-xs text-slate-500 mt-1">by {log.changed_by} ({log.user_role})</p>
                      </div>
                      <span className="text-xs text-slate-500">{new Date(log.timestamp).toLocaleString()}</span>
                    </div>
                    {log.remarks && (
                      <p className="text-sm text-slate-600 mt-2 italic">"{log.remarks}"</p>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-center text-slate-500 py-8">No audit logs available</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Sidebar>
  );
}
